import {deepClone, StorageUtils} from 'xmgoing-utils';
import {useGridTableStore} from "@/store/modules/gridTableStore";
import {saveUserColDataFun, useColSaveHooks} from "@/main-page/hooks/useColSaveHooks";
import {useDefFieldHooks} from "@/main-page-extend/containers/userDefField/hooks/useDefFieldHooks";
import {
  applySubTableSelectColConfig,
  normalizeUserDefSelectField
} from "@/main-page-extend/containers/userDefField/data/defFieldData";
import eventBus from "@/events/eventBus";
import GoingUtils from "@/utils/goingUtils";
import {isNestedDisplaySubTableColumn} from '@/main-page/utils/xmSubTableColumnUtils';

/**
 * 获取列宽
 * @param itemWid
 */
function getItemWidth(itemWid: any) {
  //计算宽度
  if ((itemWid + "").indexOf("%") < 0) {
    if (!isNaN(parseFloat(itemWid))) {
      return parseFloat(itemWid);
    } else {
      //给个默认值
      return 150;
    }
  } else {
    return itemWid;
  }
}

/**
 * 获取子列但属性
 * @param labels
 * @param itemDb
 */
function getSonItem({labels, itemDb}: any) {
  const sonItem: any = deepClone(itemDb);
  delete sonItem.children;
  sonItem.title = labels[1];
  sonItem.resizable = true;
  sonItem.key = itemDb.propId;
  sonItem.dataIndex = itemDb.propId;
  sonItem.width = getItemWidth(itemDb.propWidth || itemDb.width);
  if (!itemDb.fixed) {
    delete sonItem.fixed
  }
  return sonItem;
}

/**
 * 初始化分组列
 * @param itemDb
 * @param comFileds
 * @param newCols
 */
function initGroupCols({itemDb, comFileds, newCols}: any) {
  const labels = (itemDb.propName + '').split("#");
  //如果是第一列 构建数据
  if (comFileds[(labels[0] + "").trim()] == null) {
    itemDb.title = (labels[0] + "").trim();
    if (itemDb.children == null) {
      itemDb.children = [];
      itemDb.isCanEditor = true;
      itemDb.resizable = true;
      itemDb.children.push(getSonItem({labels, itemDb}));
      comFileds[(labels[0] + "").trim()] = itemDb;
      itemDb.filters = [
        {text: 'Male', value: 'male'},
        {text: 'Female', value: 'female'},
      ]
      newCols.push(itemDb);
    }
  } else {
    //如果是第二列 则组装数据
    comFileds[(labels[0] + "").trim()].children.push(getSonItem({labels, itemDb}));
  }
}

/**
 * 判断当前列是否授权
 * @param colAclList
 * @param colItem
 */
function isAclCol(colAclList, colItem) {
  const aclItem = colAclList.filter((itemDb) => {
    return itemDb.code === colItem.propId;
  })[0];
  if (aclItem) {
    return true;
  } else {
    return false;
  }
}

/**
 * 验证和处理列的fixed属性
 * @param cols 列数组
 */
const validateFixedColumns = function (cols: any[]) {
  // 处理fixed为left的列
  for (let i = 0; i < cols.length; i++) {
    const col = cols[i];
    // 如果当前列fixed为left
    if (col.fixed === 'left') {
      // 如果不是第一个元素，且前一个元素不是fixed为left，则将当前列的fixed置空
      if (i > 0 && cols[i - 1].fixed !== 'left') {
        delete col.fixed;
      }
    }
  }

  // 处理fixed为right的列（从后往前遍历）
  for (let i = cols.length - 1; i >= 0; i--) {
    const col = cols[i];
    // 如果当前列fixed为right
    if (col.fixed === 'right') {
      // 如果不是最后一个元素，且后一个元素不是fixed为right，则将当前列的fixed置空
      if (i < cols.length - 1 && cols[i + 1].fixed !== 'right') {
        delete col.fixed;
      }
    }
  }
}

async function initColFilter(itemDb) {
  //判断是否开启了过滤
  if (itemDb.filterType) {
    if (itemDb.filterType === 'relDomainFilter') {
      //设置为输入过滤
      itemDb.customFilterDropdown = true;
      return
    }
    //判断是否为下拉过滤
    if (itemDb.filterList && itemDb.filterList.length > 0) {
      const filterAry: any = [];
      itemDb.filterList.forEach((recordItem) => {
        filterAry.push({text: recordItem.label, value: recordItem.value});
      })
      itemDb.filters = filterAry;
      itemDb.customFilterDropdown = true;
    } else {
      //设置为输入过滤
      itemDb.customFilterDropdown = true;
    }
  }
}

/**
 * 对表格列对属性进行相应初始化
 * @param itemDb
 */
function initColProps({itemDb, dbColMap}) {
  itemDb.title = itemDb.propName;
  itemDb.key = itemDb.propId;
  itemDb.dataIndex = itemDb.propId;
  itemDb.resizable = true;
  if (itemDb['controlType'] !== 'operation') {
    const widthFromConfig = getItemWidth((itemDb.propWidth || itemDb.width));
    const widthFromDb = dbColMap[itemDb['dataIndex']]?.propWidth || dbColMap[itemDb['dataIndex']]?.width;
    // 确保列宽始终有一个有效值，使用 || 运算符确保 fallback 到默认值
    itemDb.width = widthFromConfig || (widthFromDb && getItemWidth(widthFromDb)) || 150;
  } else {
    itemDb.width = getItemWidth((itemDb.propWidth || itemDb.width)) || 150;
  }
  if (itemDb.ellipsis) {
    itemDb.ellipsis = true;
  }
  if (dbColMap[itemDb['dataIndex']]?.fixed !== undefined && dbColMap[itemDb['dataIndex']]?.fixed !== null) {
    const dbFixed = dbColMap[itemDb['dataIndex']]?.fixed;
    // 当数据库中的fixed值与当前值不同时更新
    if (itemDb?.fixed !== dbFixed) {
      // 只有当数据库中的fixed值有效时才覆盖，空字符串、null、undefined不覆盖
      if (dbFixed === 'left' || dbFixed === 'right') {
        itemDb.fixed = dbFixed;
      } else {
        delete itemDb.fixed;
      }
    }
  }
  if (dbColMap[itemDb['dataIndex']] && itemDb.propName !== dbColMap[itemDb['dataIndex']]?.fixed) {
    itemDb.propName = dbColMap[itemDb['dataIndex']]?.propName;
    itemDb.title = dbColMap[itemDb['dataIndex']]?.propName;
  }
  itemDb.ellipsis = true;
  initColFilter(itemDb);
}

const getEditorType = function (cellShowType) {
  if (cellShowType === 'xmInput') {
    return cellShowType + 'Editor'
  }
  if (cellShowType === 'xmSelect') {
    return 'SelectEditor'
  }
  return 'xmInputEditor'
}
/**
 * 根据数据库定义的字段 转换成显示用的cols
 * @param fieldList
 * @param existingColumns 已存在的列，用于去重
 * @param ctlFrom
 * @param tableKey 表格的key，用于日志
 * @param allowOverride 是否允许自定义字段覆盖系统字段，默认 false
 */
const getUserColList = function (fieldList, existingColumns, ctlFrom, tableKey, allowOverride = false) {
  const userList: any = [];

  // 只在需要去重且不允许覆盖时创建集合
  const existingDataIndexes = new Set();
  if (!allowOverride && existingColumns && Array.isArray(existingColumns)) {
    // 优化：只收集自定义字段，不收集系统字段
    existingColumns.forEach(col => {
      if (col.dataIndex && col.isUserDefEl) {
        existingDataIndexes.add(col.dataIndex);
      }
    });
  }

  fieldList.forEach((userCol) => {
    if (userCol.model) {
      const dataIndex = userCol.model.key;

      // 只在不允许覆盖时检查重复，且只检查自定义字段的重复
      if (!allowOverride && existingDataIndexes.has(dataIndex)) {
        console.warn(`[表格列加载] 跳过重复的自定义字段 - dataIndex: ${dataIndex}, 表格Key: ${tableKey || 'unknown'}`, {
          dataIndex: dataIndex,
          fieldLabel: userCol.props?.label,
          timestamp: new Date().toISOString()
        });
        return; // 跳过重复字段
      }

      const componentKey = userCol.componentKey || userCol.props?.componentKey || 'xmInput';
      const jsonItem = {
        title: userCol.props.label,
        isUserDefEl: true,
        dataIndex: dataIndex,
        key: userCol.props.key,
        required: (userCol.props.required + '') === 'true' ? true : false,
        ctlSize: 'small',
        ctlName: 'cellEditor',
        showContent: 'cellContent',
        "propName": userCol.props.label,
        "propId": userCol.model.key,
        "propType": componentKey,
        "showFlag": true,
        "controlType": componentKey === 'xmSelect'
          ? 'SelectEditor'
          : ((unref(ctlFrom) === 'xmSubTable' || unref(ctlFrom) === 'materialsSel') ? getEditorType(componentKey) : componentKey),
      }
      jsonItem.dataConfig = deepClone(userCol.dataConfig || {});
      normalizeUserDefSelectField(jsonItem, userCol);
      userList.push(jsonItem);

      // 将新添加的 dataIndex 也加入到集合中，避免同一次调用中的重复
      existingDataIndexes.add(dataIndex);
    }
  })
  return userList;
}

const USER_DEF_CHANG_CTLS = ['xmSubTable', 'materialsSel'];

/**
 * 把控件上的自定义字段值变更写到对上号的自定义列。
 * 字段编码去掉首尾空白后与 propId 全等，区分大小写。
 * 空编码、空函数、对不上的列跳过。同一编码多行时靠后的覆盖靠前的。
 */
export function applyUserDefColChangFun(columns, changList, ctlFrom) {
  const from = unref(ctlFrom);
  if (USER_DEF_CHANG_CTLS.indexOf(from) < 0 || !Array.isArray(columns)) {
    return;
  }
  const funByCode = new Map();
  if (Array.isArray(changList)) {
    changList.forEach((item) => {
      if (!item) {
        return;
      }
      const code = item.fieldCode == null ? '' : String(item.fieldCode).trim();
      const funText = item.changFun == null ? '' : String(item.changFun);
      if (!code || funText.trim() === '') {
        return;
      }
      funByCode.set(code, funText);
    });
  }
  columns.forEach((col) => {
    if (!col || !col.isUserDefEl || col.propId == null) {
      return;
    }
    const propId = String(col.propId);
    if (funByCode.has(propId)) {
      col.changFun = funByCode.get(propId);
    } else {
      delete col.changFun;
    }
  });
}

/**
 * 根据数据库存的列信息 初始化当前的列信息
 * @param dbColMap
 * @param colItem
 */
const initColStateByDbCol = function (dbColMap, colItem) {
  if (unref(dbColMap) == null) {
    dbColMap = {};
  }
  initColProps({itemDb: colItem, dbColMap});
  let dbColItem: any = null;
  if (colItem['dataIndex']) {
    dbColItem = unref(dbColMap)[colItem['dataIndex']]
  } else {
    dbColItem = unref(dbColMap)[colItem['propId']];
  }
  //如果存在数据库的列信息
  if (dbColItem) {
    if(dbColItem&&dbColItem.isDelete){
      colItem.isDelete = dbColItem.isDelete;
    }
    // 处理列固定属性
    if (dbColItem.fixed === 'left' || dbColItem.fixed === 'right') {
      // 数据库配置明确指定固定方向，直接应用
      colItem.fixed = dbColItem.fixed;
    } else {
      // 数据库配置为不固定（空字符串、null、undefined或'false'）
      delete colItem.fixed;
    }
    // 使用数据库中的宽度，如果为空则保留 initColProps 中设置的默认值
    if (dbColItem.width && dbColItem.width > 0) {
      colItem.width = dbColItem.width;
    }
    //如果配置是显示 默认是不显示
    if ((colItem.showFlag === false || colItem.showFlag === 'false')) {
      if (dbColItem.showFlag == true || dbColItem.showFlag === 'true') {
        colItem.showFlag = true;
      }
    }
    //如果配置不显示
    if ((dbColItem.showFlag === false || dbColItem.showFlag === 'false')) {
      colItem.showFlag = false;
    }

  }
}
/**
 * 分组列信息
 * @param itemDb
 */
const isGroupName = function (itemDb): boolean {
  if ((itemDb.propName + '').indexOf("#") > 0) {
    return true;
  } else {
    return false;
  }
}
/**
 * 设置一些特殊列的信息
 * @param allCols
 * @param curUserTenantId
 * @param orderNumFixed 序号列左固定，默认开启；为 false 时不再强制 fixed，以列配置为准
 */
const initColState = function (allCols, curUserTenantId, roleAppConfigs, orderNumFixed = true) {
  const forceOrderNumFixed = (orderNumFixed + '') !== 'false';
  //设置排序号属性
  unref(allCols).forEach((colItem) => {
    if (colItem.controlType === 'orderNum') {
      if (forceOrderNumFixed) {
        colItem.fixed = 'left';
      }
      colItem.align = 'center';
    }
    if ((unref(colItem)['roleAppAcl'] + '') === 'true') {
      if ((unref(roleAppConfigs)[unref(colItem).roleAppAclField] + '') === (unref(colItem).roleAppAclValue + '')) {
        colItem.showFlag = false;
      }
    }
    //是否显示总部 而且当前租户为总部
    if ((colItem.showFlagIsRoot + '') === 'true' && !((window as any).appObj && (window as any).appObj.isDes) && curUserTenantId === '000000') {
      colItem.showFlag = false;
    }
  })

}
const getSeepCols = function ({ctlAclInfos, propName = 'tableList', props}) {
  //根据权限找出需要隐藏的列信息
  const skeepColList = {};
  if (unref(ctlAclInfos) && Array.isArray(unref(ctlAclInfos))) {
    unref(ctlAclInfos).forEach((aclRecord) => {
      if (unref(aclRecord)[propName] && Array.isArray(unref(aclRecord)[propName]) && unref(aclRecord)[propName].length > 0) {
        const selItem = unref(aclRecord)[propName].filter((itemDb) => {
          return itemDb.key === props.key
        })[0];
        if (selItem && selItem['colShowConfigs']) {
          selItem['colShowConfigs'].forEach((colItemTemp) => {
            if (colItemTemp.showFlag === false) {
              skeepColList[colItemTemp.propId] = false;
            }
          })
        }
      }
    })
  }
  return skeepColList;
}
/**
 * 获取没有权限的列信息
 * @param ctlAclInfos
 */
export const getAclSkeepCols = function (ctlAclInfos, props, ctlFrom) {
  if (unref(ctlFrom) === 'xmSubTable') {
    return getSeepCols({ctlAclInfos, propName: 'subTableList', props});
  } else if (unref(ctlFrom) === 'materialsSel') {
    return getSeepCols({ctlAclInfos, propName: 'materialsSelList', props});
  } else {
    return getSeepCols({ctlAclInfos, propName: 'tableList', props});
  }
}

/**
 * 获取按钮授权信息
 * @param ctlAclInfos
 * @param props
 */
export const getAclOpertionBtns = function (ctlAclInfos, props) {
  //根据权限找出需要隐藏的列信息
  const skeepColList = {};
  if (unref(ctlAclInfos) && Array.isArray(unref(ctlAclInfos))) {
    unref(ctlAclInfos).forEach((aclRecord) => {
      if (aclRecord['tableList'] && Array.isArray(aclRecord['tableList']) && aclRecord['tableList'].length > 0) {
        const selItem = aclRecord['tableList'].filter((itemDb) => {
          return itemDb.key === props.key
        })[0];
        if (selItem && selItem['operationConfigs']) {
          selItem['operationConfigs'].forEach((colItemTemp) => {
            skeepColList[colItemTemp.key] = colItemTemp.btnAcl;
          })
        }
      }
    })
  }
  return skeepColList;
}

/**
 * 根据 Map 设置列的属性（支持多次调用，包括子列）
 */
function applyColPropsFromMap(cols: any[], map: Map<any, any>, propName: string) {
  if (map.size > 0) {
    cols.forEach((colItem) => {
      if (map.has(colItem.dataIndex)) {
        colItem[propName] = map.get(colItem.dataIndex);
      }
      // 处理子列
      if (colItem.children && Array.isArray(colItem.children)) {
        colItem.children.forEach((childCol) => {
          if (map.has(childCol.dataIndex)) {
            childCol[propName] = map.get(childCol.dataIndex);
          }
        });
      }
    });
  }
}

/**
 * 列配置信息
 * @param ctlAclInfos
 * @param dataConfig
 * @param props
 * @param colConfig
 * @param treeGrid
 */
export function useTableColumns({
                                  ctlFrom = 'commonGrid',
                                  ctlAclInfos,
                                  dataConfig,
                                  roleAppConfigs,
                                  userFieldConfigCols,
                                  readOnlyText = false,
                                  props,
                                  colConfig,
                                  subGridKey,
                                  mainPageColConfigCode
                                }, treeGrid = false) {
  const gridTableStore: any = useGridTableStore();
  const {loadUserCols} = useDefFieldHooks({props});
  const columsLoading = ref(false);
  const tableObj = ref(gridTableStore.getGridObjByKey(unref(props).key));
  const columns = ref([]);
  const subTableColumns = ref([]);
  const userColums = ref([]);
  // 保存页面初始列顺序，重置时还原为系统默认配置
  const originDataColumns = deepClone(unref(dataConfig)["columns"] || []);
  let gridColumnsRequestId = 0;
  // 列初始化的代次。超时兜底和接口返回同时写列时，只保留后开始的那次。
  let columnsApplySeq = 0;
  // 当前 store 里的列来自超时或请求失败的页面设计列，下一次真实结果需要覆盖它。
  let columnsFromFallback = false;
  let columnsFallbackTimer = null;
  const clearColumnsFallback = () => {
    if (columnsFallbackTimer) {
      clearTimeout(columnsFallbackTimer);
      columnsFallbackTimer = null;
    }
  };
  const startColumnsApply = () => {
    clearColumnsFallback();
    return ++columnsApplySeq;
  };
  const markColumnsReady = () => {
    columsLoading.value = true;
    clearColumnsFallback();
  };
  // 使用 Map 存储每个字段的禁用状态，key 为字段名，value 为是否禁用
  const colDisabledMap = ref(new Map());
  // 使用 Map 存储每个字段的显示/隐藏状态，key 为字段名，value 为是否显示
  const colShowMap = ref(new Map());
  // 使用 Map 存储每个字段的只读状态，key 为字段名，value 为是否只读
  const colReadOnlyMap = ref(new Map());
  // 标志：是否需要强制更新 store 中的列配置
  const shouldForceUpdateCols = ref(false);

  const {
    initColSByDbCol,
    handleResizeColumn
  } = useColSaveHooks({props, columns});

  /**
   * 初始化列显示信息
   * @param dbColMap
   */
  const initShowColsInfo = function (dbColMap, colSot, applySeq, provisional = false) {
    const newCols: any = [];
    const comFileds: any = {};
    /**
     * 获取数据集合里面定义的字段信息
     */
    const rawColumns = unref(dataConfig)["columns"];
    const colsListDb: any = deepClone(Array.isArray(rawColumns) ? rawColumns : []);
    const skeepColList = getAclSkeepCols(ctlAclInfos, props, ctlFrom);
    const colsList: any = [];
    colsListDb.forEach((colItem, index) => {
      if (skeepColList && colItem.propId && (skeepColList[colItem.propId] + '') === 'false') {
        return
      } else {
        colsList.push(colItem);
      }
    })
    /**
     * 如果存在自定义字段 则需要追加自定义字段
     * 移除配置列中与自定义字段同名的非自定义列，避免无 controlType 的列导致纯文本展示
     */
    if (unref(userColums) && unref(userColums).length > 0) {
      const userDefKeys = new Set(
        unref(userColums).map((col) => col.dataIndex || col.propId).filter(Boolean)
      );
      for (let i = colsList.length - 1; i >= 0; i--) {
        const key = colsList[i].dataIndex || colsList[i].propId;
        if (key && userDefKeys.has(key) && !colsList[i].isUserDefEl) {
          colsList.splice(i, 1);
        }
      }
      colsList.splice(colsList.length - 1, 0, ...unref(userColums));
    }
    colsList && Array.isArray(colsList) && colsList.forEach((itemDb) => {
      applySubTableSelectColConfig(itemDb, unref(ctlFrom));
      //如果存在数据库信息 则根据保存的信息初始化列信息
      initColStateByDbCol(dbColMap, itemDb);
      //是否存在列表头分组
      if (isGroupName(itemDb)) {
        initGroupCols({itemDb, comFileds, newCols});
      } else {
        newCols.push(itemDb);
      }
    })
    /**
     * 获取当前租户信息
     */
    const {curUserTenantId} = gridTableStore.getCurUserTenantId();
    /**
     * 设置特殊列的属性
     */
    initColState(newCols, curUserTenantId, roleAppConfigs, unref(props).orderNumFixed);
    if (unref(props).columnControl) {
      const index = unref(newCols).findIndex((itemDb) => {
        return itemDb.dataIndex === 'columnsConfig'
      })
      const indexOpe = unref(newCols).findIndex((itemDb) => {
        return itemDb.propType === 'operation'
      })
      if (index < 0 && indexOpe < 0) {
        unref(newCols).push({
          dataIndex: 'columnsConfig',
          key: 'columnsConfig',
          width: 40,
          fixed: 'right',
          align: 'center',
          controlType: 'columnsConfig',
          showFlag: true
        })
      }
    }
    // 对 newCols 按照 colSot 排序，但确保orderNum类型始终在最前
    if (colSot && Object.keys(colSot).length > 0) {
      newCols.sort((a, b) => {
        // 如果a是orderNum，排在最前
        if (a.controlType === 'orderNum') return -1;
        // 如果b是orderNum，排在最后
        if (b.controlType === 'orderNum') return 1;

        const indexA = colSot[a.propId] !== undefined ? colSot[a.propId] : Infinity;
        const indexB = colSot[b.propId] !== undefined ? colSot[b.propId] : Infinity;
        return indexA - indexB;
      });
    } else {
      // 没有排序配置时，确保orderNum在最前
      const orderNumIndex = newCols.findIndex(col => col.controlType === 'orderNum');
      if (orderNumIndex > 0) {
        const orderNumCol = newCols.splice(orderNumIndex, 1)[0];
        newCols.unshift(orderNumCol);
      }
    }
    validateFixedColumns(unref(newCols));

    if (unref(ctlFrom) === 'commonGrid') {
      // 将 operation 类型的列移到最后
      const operationColIndex = newCols.findIndex(col => col.controlType === 'operation');
      if (operationColIndex >= 0) {
        const operationCol = newCols.splice(operationColIndex, 1)[0];
        newCols.push(operationCol);
      }
    }

    // 从 store 中读取列权限设置（在组件创建前通过 setColDisabled 设置的）
    const colPermissions = gridTableStore.getColPermissions(unref(props).key);
    if (Object.keys(colPermissions).length > 0) {
      // 将 store 中的权限设置填充到 Map 中
      Object.keys(colPermissions).forEach((fieldId) => {
        const permission = colPermissions[fieldId];
        switch (permission) {
          case 'readonly':
            colReadOnlyMap.value.set(fieldId, true);
            colDisabledMap.value.set(fieldId, false);
            break;
          case 'disabled':
            colDisabledMap.value.set(fieldId, true);
            colReadOnlyMap.value.set(fieldId, false);
            break;
          case 'editable':
            colReadOnlyMap.value.set(fieldId, false);
            colDisabledMap.value.set(fieldId, false);
            break;
          case true:  // 兼容旧配置：true = disabled
            colDisabledMap.value.set(fieldId, true);
            colReadOnlyMap.value.set(fieldId, false);
            break;
          case false:  // 兼容旧配置：false = editable
            colReadOnlyMap.value.set(fieldId, false);
            colDisabledMap.value.set(fieldId, false);
            break;
        }
      });
    }
    // 从 store 中读取列显示/隐藏设置（在组件创建前通过 setColHideOrShow 设置的）
    const colShows = gridTableStore.getColShows(unref(props).key);
    if (Object.keys(colShows).length > 0) {
      // 将 store 中的显示/隐藏设置填充到 Map 中
      Object.keys(colShows).forEach((fieldId) => {
        const showFlag = colShows[fieldId];
        colShowMap.value.set(fieldId, showFlag === true);
      });
    }
    // 根据 Map 设置列的属性（支持多次调用，包括子列）
    applyColPropsFromMap(newCols, colDisabledMap.value, 'disabled');
    applyColPropsFromMap(newCols, colShowMap.value, 'showFlag');
    applyColPropsFromMap(newCols, colReadOnlyMap.value, 'readOnly');
    if (applySeq != null && applySeq !== columnsApplySeq) {
      return;
    }
    subTableColumns.value = newCols.filter((itemDb) => isNestedDisplaySubTableColumn(itemDb));
    /**
     * 设置需要显示的列信息（开启嵌套展示的动态子表格列不在主表展示）
     */
    columns.value = newCols.filter((itemDb) => {
      if (isNestedDisplaySubTableColumn(itemDb)) {
        return false;
      }
      if (itemDb.isDelete) {
        return false;
      }
      return itemDb.showFlag !== false && itemDb.showFlag !== 'false';
    });
    // 列已经算完就结束等待。只有 1 列或没有可见列时也要离开骨架屏。
    markColumnsReady();
    const notDelCols=newCols.filter((itemDb) => {
      if (itemDb.isDelete) {
        return false;
      }
      return true;
    });
    /**
     * 将信息存到组件里面
     */
    const gridKey = unref(props).key;
    const hadStoreCols = !!(gridTableStore[gridKey] && gridTableStore[gridKey]["cols"]);
    const forceStore = !!shouldForceUpdateCols.value;
    gridTableStore.initCtlCols(gridKey, deepClone(notDelCols), forceStore);
    // 重置强制更新标志
    shouldForceUpdateCols.value = false;
    // 只有这次确实把页面设计列写进了空的 store，才允许下一次真实结果覆盖。store 里已有列时保持原有的只写一次。
    if (provisional && (!hadStoreCols || forceStore)) {
      columnsFromFallback = true;
    } else if (!provisional) {
      columnsFromFallback = false;
    }
  }


  const initTableCols = function (dbColMap, colSot, applySeq, usePageColumnsOnly = false, provisional = false) {
    const seq = applySeq == null ? startColumnsApply() : applySeq;
    const showPageColumns = () => {
      userColums.value = [];
      initShowColsInfo(dbColMap, colSot, seq, provisional);
    };
    const finishColumnsApply = () => {
      if (seq === columnsApplySeq) {
        markColumnsReady();
      }
    };
    try {
      //如果存在用户自定义列。超时兜底只使用页面设计列，避免再等一次接口。
      if (!usePageColumnsOnly && unref(props).showUserDefCol && unref(props).pageCode) {
        loadUserCols(unref(props).pageCode || unref(dataConfig).domainCode, (fieldList) => {
          if (seq !== columnsApplySeq) {
            return;
          }
          try {
            // 获取已存在的列，用于去重检查（优化：不需要深拷贝）
            const existingColumns = unref(dataConfig)["columns"];

            // 检查是否允许自定义字段覆盖系统字段
            const allowOverride = unref(props).allowUserColOverride || false;

            // 调用 getUserColList 时传入已存在的列，自动过滤重复的 dataIndex
            userColums.value = getUserColList(fieldList, existingColumns, unref(ctlFrom), unref(props).key, allowOverride);
            applyUserDefColChangFun(userColums.value, unref(props).userDefChangList, ctlFrom);

            console.log(`[表格列加载] 处理用户自定义列 - 表格Key: ${unref(props).key}, 原始字段数量: ${fieldList.length}, 去重后数量: ${userColums.value.length}, 允许覆盖: ${allowOverride}`, {
              tableKey: unref(props).key,
              originalFieldCount: fieldList.length,
              filteredFieldCount: userColums.value.length,
              skippedCount: fieldList.length - userColums.value.length,
              allowOverride: allowOverride,
              timestamp: new Date().toISOString()
            });

            initShowColsInfo(dbColMap, colSot, seq, provisional);
          } catch (error) {
            console.error(error);
            showPageColumns();
          } finally {
            finishColumnsApply();
          }
        }, 'subTable')
      } else {
        showPageColumns();
        finishColumnsApply();
      }
    } catch (error) {
      console.error(error);
      finishColumnsApply();
    }
  }

  watch(() => unref(props).userDefChangList, () => {
    applyUserDefColChangFun(unref(userColums), unref(props).userDefChangList, ctlFrom);
  }, {deep: true});

  // 列配置请求一直不返回时，用页面设计里的列把表格显示出来。
  const scheduleColumnsFallback = (requestId) => {
    clearColumnsFallback();
    const seqAtSchedule = columnsApplySeq;
    columnsFallbackTimer = setTimeout(() => {
      columnsFallbackTimer = null;
      if (requestId !== gridColumnsRequestId || seqAtSchedule !== columnsApplySeq) {
        return;
      }
      initTableCols({}, {}, undefined, true, true);
    }, 2000);
  };


  //将列的显示设置为计算属性
  const getGridColumns = function () {
    const requestId = ++gridColumnsRequestId;
    const applyGridColumns = (dbColMap, colSot, captureEnterPage, provisional = false) => {
      if (requestId !== gridColumnsRequestId) {
        return;
      }
      // 覆盖超时或失败时写下的页面设计列，不影响 store 里已经有的正式列配置。
      if (!provisional && columnsFromFallback) {
        shouldForceUpdateCols.value = true;
      }
      const applySeq = startColumnsApply();
      if (captureEnterPage) {
        gridTableStore.captureEnterPageColConfig(unref(props).key, dbColMap || {});
      }
      initTableCols(dbColMap, colSot, applySeq, false, provisional);
    };
    //是否支持列宽度配置 需要从数据库获取
    if (unref(colConfig) !== false && !((window as any).appObj && (window as any).appObj.isDes)) {
      if (!columsLoading.value) {
        scheduleColumnsFallback(requestId);
      }
      initColSByDbCol((dbColMap, colSot, requestFailed) => {
        applyGridColumns(dbColMap, colSot, !requestFailed, !!requestFailed);
      }, userFieldConfigCols,subGridKey,mainPageColConfigCode);
    } else {
      applyGridColumns({}, {}, false);
    }
  }

  getGridColumns();
  let timer = null;

  const flushColsFun = function () {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      getGridColumns();
    }, 200)
  }

  /**
   * 刷新列信息
   */
  const flushColsWatch = watch(() => unref(tableObj)?.['flushCols'], () => {
    if (!unref(tableObj)) {
      return;
    }

    const newCols = gridTableStore[unref(props).key] && gridTableStore[unref(props).key]["cols"];
    if (newCols && Array.isArray(newCols)) {
      // 同步排序到 dataConfig.columns
      const currentColumns = unref(dataConfig)["columns"];
      // 检查 currentColumns 是否存在且为数组
      if (!currentColumns || !Array.isArray(currentColumns)) {
        return;
      }
      // 创建一个 map，以 propId 作为 key 来快速查找
      const columnMap = new Map(currentColumns.map(col => [col.propId, col]));
      // 按照 newVal 的顺序重新排列 currentColumns
      const reorderedColumns = newCols
        .filter(itemDb => columnMap.has(itemDb.propId)) // 确保只保留存在的列
        .map(itemDb => columnMap.get(itemDb.propId));   // 获取原始配置对象
      // 更新 dataConfig.columns 顺序
      currentColumns.splice(0, currentColumns.length, ...reorderedColumns);
    }
    flushColsFun();
  })

  let tirmerWatch = null;
  let stopWatcher = null;
  let flushTimer = null;

  /**
   * 变更列信息
   * @param resetToDefault 为 true 时表示用户主动重置，恢复系统默认列配置
   * @param subGridListScope
   */
  const _changeGridCols = ({resetToDefault, subGridListScope}) => {
    columns.value = [];
    shouldForceUpdateCols.value = true; // 强制更新
    if (resetToDefault) {
      // 取消待执行的列刷新，避免重置后被旧的 debounce 回调覆盖
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      // 使进行中的异步列加载失效，防止旧请求在重置后覆盖结果
      gridColumnsRequestId++;
      // 还原 dataConfig.columns 为系统默认顺序（flushColsWatch 可能已将其改为用户自定义顺序）
      if (originDataColumns && originDataColumns.length > 0) {
        const currentColumns = unref(dataConfig)["columns"];
        currentColumns.splice(0, currentColumns.length, ...deepClone(originDataColumns));
      }
      // 重置时直接使用系统默认配置，不再异步读取数据库（避免与延迟保存产生竞态）
      initTableCols({}, {});
      return;
    }
    getGridColumns();
  }
  /**
   * 设置列不可以编辑
   * @param fieldList
   * @param disFlag
   */
  const _setColDisabled = ({fieldList, permission}) => {
    const list = unref(fieldList) || [];
    list.forEach((fieldId) => {
      // 兼容旧配置：如果permission是布尔值，则按旧逻辑处理
      if (permission === true || permission === false || permission === 'true' || permission === 'false') {
        colDisabledMap.value.set(fieldId, (permission == true || permission == 'true'));
        colReadOnlyMap.value.set(fieldId, false);
      } else {
        // 新配置：根据枚举值设置列状态
        // 'readonly' -> 只读: readOnly=true, disabled=false
        // 'disabled' -> 禁用: readOnly=false, disabled=true
        // 'editable' -> 编辑: readOnly=false, disabled=false
        switch (permission) {
          case 'readonly':
            colReadOnlyMap.value.set(fieldId, true);
            colDisabledMap.value.set(fieldId, false);
            break;
          case 'disabled':
            colDisabledMap.value.set(fieldId, true);
            colReadOnlyMap.value.set(fieldId, false);
            break;
          case 'editable':
            colReadOnlyMap.value.set(fieldId, false);
            colDisabledMap.value.set(fieldId, false);
            break;
        }
      }
    });
    shouldForceUpdateCols.value = true;
    getGridColumns();
  }
  /**
   * 设置列不可以编辑
   * @param fieldList
   * @param disFlag
   */
  const _setColHideOrShow = ({fieldList, disFlag}) => {
    const list = unref(fieldList) || [];
    list.forEach((fieldId) => {
      colShowMap.value.set(fieldId, disFlag === true);
    });
    shouldForceUpdateCols.value = true;
    getGridColumns();
  }

  onMounted(() => {
    //新增编辑表格
    eventBus.off(GoingUtils.getEventCtlKey(unref(props).key) + '_setColDisabled', _setColDisabled);
    eventBus.on(GoingUtils.getEventCtlKey(unref(props).key) + '_setColDisabled', _setColDisabled);
    //列显示隐藏
    eventBus.off(GoingUtils.getEventCtlKey(unref(props).key) + '_setColHideOrShow', _setColHideOrShow);
    eventBus.on(GoingUtils.getEventCtlKey(unref(props).key) + '_setColHideOrShow', _setColHideOrShow);
    //新增编辑表格
    eventBus.off(GoingUtils.getEventCtlKey(unref(props).key) + '_changeGridCols', _changeGridCols);
    eventBus.on(GoingUtils.getEventCtlKey(unref(props).key) + '_changeGridCols', _changeGridCols);
  })

  onBeforeUnmount(() => {
    eventBus.off(GoingUtils.getEventCtlKey(unref(props).key) + '_setColDisabled', _setColDisabled);
    eventBus.off(GoingUtils.getEventCtlKey(unref(props).key) + '_setColHideOrShow', _setColHideOrShow);
    eventBus.off(GoingUtils.getEventCtlKey(unref(props).key) + '_changeGridCols', _changeGridCols);
    eventBus.off(unref(props).key + '_gridQueryScope', {});

    // 清理 store 中持久化的列权限和显示状态
    gridTableStore.clearColPermissions(unref(props).key);
    gridTableStore.clearColShows(unref(props).key);

    flushTimer && clearTimeout(flushTimer);
    tirmerWatch && clearTimeout(tirmerWatch);
    timer && clearTimeout(timer);
    clearColumnsFallback();
    stopWatcher && stopWatcher();
    flushColsWatch && flushColsWatch();
  })

  const saveColumnsConfig = function ({columnsInfo, onSuccess, onError}) {
    saveUserColDataFun(columnsInfo, props, () => {
      shouldForceUpdateCols.value = true;
      gridTableStore.initCtlCols(unref(props).key, deepClone(columnsInfo), true);
      getGridColumns();
      onSuccess && onSuccess();
    }, (err) => {
      onError && onError(err);
    });
  }


  return {
    columsLoading,
    saveColumnsConfig,
    handleResizeColumn,
    columns,
    subTableColumns
  }
}
